import React, { createContext, useContext, useState, useEffect } from 'react';
import { initialData } from '../utils/mockData';

const StoreContext = createContext();

export const useStore = () => useContext(StoreContext);

export const StoreProvider = ({ children }) => {
  const [data, setData] = useState(() => {
    const saved = localStorage.getItem('midtermRentalsData');
    return saved ? JSON.parse(saved) : initialData;
  });

  useEffect(() => {
    localStorage.setItem('midtermRentalsData', JSON.stringify(data));
  }, [data]);

  const updateData = (key, newData) => {
    setData(prev => ({ ...prev, [key]: newData }));
  };

  const addRecord = (key, record) => {
    const newRecord = { ...record, id: Date.now().toString() + Math.random().toString(36).slice(2, 6) };
    setData(prev => ({ ...prev, [key]: [...(prev[key] || []), newRecord] }));

    // Auto-log sensitive deletions or exports if triggered from components
    if (key === 'transactions' || key === 'properties') {
       logAccess(`Create ${key.slice(0, -1)}`, 'Admin', 'Success');
    }
  };

  const updateRecord = (key, id, updatedRecord) => {
    setData(prev => ({
      ...prev,
      [key]: (prev[key] || []).map(item => item.id === id ? { ...item, ...updatedRecord } : item)
    }));
  };

  const deleteRecord = (key, id) => {
    const item = (data[key] || []).find(i => i.id === id);
    setData(prev => ({
      ...prev,
      [key]: (prev[key] || []).filter(item => item.id !== id)
    }));
    
    // Log deletion of sensitive records
    logAccess(`Delete ${key.slice(0, -1)}: ${item?.name || item?.id}`, 'Admin', 'Success');
  };

  const clearAllData = () => {
    setData({
      propertyVisits: [],
      utilityBills: [],
      renewalSettings: { enabled: true, noticeDaysBefore: 60, followUpDaysAfter: 7, defaultTemplateId: '', autoEscalate: true },
      properties: [],
      tenants: [],
      leases: [],
      rentPayments: [],
      maintenance: [],
      assets: [],
      documentTemplates: [],
      onboardingChecklists: [],
      communicationTemplates: [],
      documents: [],
      stayHistory: [],
      transactions: [],
      recurringExpenses: [],
      recurringRevenue: [],
      preventativeSchedules: [],
      customFieldDefs: [],
      transactionTags: ['Tax Deductible', 'Recurring', 'Urgent', 'Turnover', 'Verified'],
      feedback: [],
      communications: [],
      accessLogs: [],
      incomeCategories: ['Rent', 'Pet Fee', 'Cleaning Fee'],
      expenseCategories: ['Maintenance', 'Utilities', 'Software', 'Cleaning'],
    });
  };

  const logAccess = (event, userId = 'Admin', status = 'Success') => {
    const newLog = {
      id: `log_${Date.now()}`,
      userId,
      event,
      timestamp: new Date().toISOString(),
      ip: '192.168.1.1', // Simulated
      device: navigator.userAgent.split(') ')[1]?.split(' ')[0] || 'Web Browser',
      status
    };
    setData(prev => ({
      ...prev,
      accessLogs: [newLog, ...(prev.accessLogs || [])].slice(0, 100) // Keep last 100 logs
    }));
  };

  return (
    <StoreContext.Provider value={{ data, updateData, addRecord, updateRecord, deleteRecord, clearAllData, logAccess }}>
      {children}
    </StoreContext.Provider>
  );
};